// The floor picker on the board (qa-runs#1160; design/32 §5): the DM moves between every floor of the scene and edits
// them (name, height, ceiling, open air, order, add, remove — removing one that holds things asks where they go); a
// player switches between the floors their tokens are on (the only ones they were sent). Beside it, each person's own
// switch for the height tooltip. A scene with one floor shows the DM only "Floors…", a player nothing.
import { useEffect, useState } from 'react';
import type { AssetInfo } from '@dragons-table/vtt-shared';
import { listAssets } from '../assets';
import { authFetch } from '../session';
import { DEFAULT_FLOORS, FLOOR_LIMITS, floorBelow, floorOf, sceneFloors, type Floor } from '@dragons-table/vtt-shared';
import { useShell } from '../shell/context';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { floorView, heightTipOn, onHeightTip, setHeightTip, viewedFloor } from './floorView';

const btn = 'vtt-btn px-2 py-0.5 text-xs';
const newId = (): string => `f-${Math.random().toString(36).slice(2, 8)}`;

export function FloorPicker() {
  const { store, meId, act, systemRules } = useTableState();
  const mayAct = useMayAct();
  const isDm = mayAct('scene.set');
  const [picked, setPicked] = useState(floorView.get());
  useEffect(() => floorView.subscribe(setPicked), []);
  const [tip, setTip] = useState(heightTipOn());
  useEffect(() => onHeightTip(setTip), []);
  const [editing, setEditing] = useState(false);
  const scene = store.state.scene;
  const floors = scene.floors ?? [];
  const objects = store.state.objects as Record<string, { floor?: string; owners?: string[]; mine?: boolean; z?: number }>;
  const anyHeight = Object.values(objects).some((o) => typeof o.z === 'number');
  if (!isDm && floors.length < 2 && !anyHeight) return null;
  const current = viewedFloor({ scene, objects }, meId, isDm, picked);
  return <FloorPickerBody {...{ isDm, floors, current, tip, editing, setEditing, scene, store, act, systemRules }} />;
}

function FloorPickerBody({ isDm, floors, current, tip, editing, setEditing, scene, store, act, systemRules }: {
  isDm: boolean; floors: Floor[]; current: string | null; tip: boolean; editing: boolean; setEditing: (v: boolean) => void;
  scene: ReturnType<typeof useTableState>['store']['state']['scene']; store: ReturnType<typeof useTableState>['store']; act: ReturnType<typeof useTableState>['act']; systemRules: ReturnType<typeof useTableState>['systemRules'];
}) {
  // Openings (slice B): while on, a press on the board marks or clears that square of the viewed floor as open onto the
  // floor below — a stair, a ladder, a pit, a balcony's edge. Each press is one scene.set.
  const [marking, setMarking] = useState(false);
  // The phone layout (issue 1242): one button in the corner until it is wanted.
  const { phone = false } = useShell();
  const [folded, setFolded] = useState(true);
  const latest = { floors, current, scene };
  useEffect(() => {
    if (!marking || !isDm) { floorView.setMarker(null); return undefined; }
    floorView.setMarker((at) => {
      const { floors: fs, current: cur, scene: sc } = latest;
      const k = `${at.i},${at.j}`;
      const next = fs.map((f) => {
        if (f.id !== cur) return f;
        const had = f.openings ?? [];
        const openings = had.includes(k) ? had.filter((x) => x !== k) : [...had, k].slice(0, FLOOR_LIMITS.openings);
        const { openings: _o, ...rest } = f;
        return openings.length ? { ...rest, openings } : rest;
      });
      const { floors: _f, ...rest } = sc;
      act({ kind: 'scene.set', scene: { ...rest, floors: next } });
    });
    return () => floorView.setMarker(null);
  });
  const here = floors.find((f) => f.id === current);
  // In the phone layout (issue 1242) the picker is one button in the corner until it is wanted.
  if (phone && folded) {
    return (
      <button type="button" className="vtt-chrome pointer-events-auto absolute bottom-2 right-[6.75rem] z-20 rounded-[3px] border border-vtt-line bg-vtt-chrome/[0.88] px-2.5 py-1 text-xs text-vtt-soft"
        aria-expanded={false} data-testid="floor-picker-open" onClick={() => setFolded(false)}>Floors</button>
    );
  }
  return (
    <div className={`pointer-events-auto absolute ${phone ? 'bottom-1 right-1 max-h-[calc(100%-0.5rem)] overflow-y-auto' : 'bottom-16 left-2'} z-20 flex flex-col items-start gap-1 vtt-popover px-2 py-1 text-xs`} data-testid="floor-picker">
      {phone && <button type="button" className={btn} aria-expanded data-testid="floor-picker-close" onClick={() => setFolded(true)}>Close</button>}
      {floors.length > 1 && (
        <div role="radiogroup" aria-label="Floor" className="flex flex-col-reverse gap-0.5">
          {floors.map((f) => (
            <button key={f.id} type="button" role="radio" aria-checked={current === f.id} data-testid={`floor-${f.id}`}
              className={`${btn} ${current === f.id ? 'ring-1 ring-vtt-gold' : ''}`} onClick={() => floorView.set(f.id)}>{f.name}</button>
          ))}
        </div>
      )}
      <label className="flex items-center gap-1" title="Hovering a token (a long press on touch) shows its height and your selected token's distance to it. Only for you.">
        <input type="checkbox" aria-label="Height tips" checked={tip} onChange={(e) => setHeightTip(e.target.checked)} /> Height tips
      </label>
      {isDm && floors.length > 1 && floors[0]?.id !== current && (
        <button type="button" className={`${btn} ${marking ? 'ring-1 ring-sky-400' : ''}`} aria-pressed={marking} data-testid="floor-mark-openings"
          title="Press squares of this floor that open onto the floor below: a stair, a ladder, a pit, a balcony's edge. Sight, light and attacks pass there, and a token on one may change floor."
          onClick={() => setMarking(!marking)}>{marking ? `Done marking (${here?.openings?.length ?? 0})` : 'Mark openings'}</button>
      )}
      {isDm && <button type="button" className={btn} onClick={() => setEditing(true)}>Floors…</button>}
      {isDm && editing && <FloorEditor onClose={() => setEditing(false)} perSquare={systemRules.distance?.perSquare ?? 1} unit={systemRules.distance?.unit ?? 'squares'}
        onSave={(next, moves) => {
          for (const m of moves) {
            const o = store.state.objects[m.id] as { x: number; y: number; rev?: number } | undefined;
            if (o) act({ kind: 'object.move', id: m.id, route: [{ x: o.x, y: o.y }], rev: o.rev ?? 0, floor: m.floor });
          }
          const { floors: _f, ...rest } = scene;
          act({ kind: 'scene.set', scene: { ...rest, ...(next.length > 1 ? { floors: next } : {}) } });
          if (!next.some((f) => f.id === current)) floorView.set(null);
          setEditing(false);
        }} />}
    </div>
  );
}

function FloorEditor({ onClose, onSave, perSquare, unit }: { onClose: () => void; onSave: (floors: Floor[], moves: Array<{ id: string; floor: string }>) => void; perSquare: number; unit: string }) {
  const { store } = useTableState();
  const old = sceneFloors(store.state.scene);
  const [draft, setDraft] = useState<Floor[]>(() => old.map((f) => ({ ...f })));
  // Where each removed floor's things go (design/32 §2): the floor below by default.
  const [dest, setDest] = useState<Record<string, string>>({});
  const objects = Object.values(store.state.objects) as Array<{ id: string; floor?: string }>;
  const removed = old.filter((f) => !draft.some((d) => d.id === f.id));
  const holding = removed.map((f) => ({ f, things: objects.filter((o) => floorOf(o, old) === f.id) })).filter((x) => x.things.length);
  const set = (k: number, patch: Partial<Floor>) => setDraft(draft.map((f, i) => (i === k ? { ...f, ...patch } : f)));
  const move = (k: number, by: number) => { const n = [...draft]; const [f] = n.splice(k, 1); n.splice(k + by, 0, f!); setDraft(n); };
  const valid = draft.length >= 1 && draft.every((f) => f.name.trim() && f.height > 0);
  // Each floor's own map (A2): one of the DM's map pictures; the ground floor without one shows the scene's map.
  const [maps, setMaps] = useState<AssetInfo[]>([]);
  useEffect(() => { void listAssets().then((a) => setMaps(a.filter((x) => x.kind === 'map'))).catch(() => setMaps([])); }, []);
  return (
    <div role="dialog" aria-label="Floors" className="mt-1 flex flex-col gap-1 rounded border border-stone-600 p-2" data-testid="floor-editor">
      <p className="text-stone-400">Bottom first. Height in {unit}; each floor starts where the one below ends.</p>
      {[...draft].map((f, k) => (
        <div key={f.id} className="flex flex-wrap items-center gap-1">
          <input aria-label="Floor name" className="vtt-input w-24 px-1" value={f.name} maxLength={FLOOR_LIMITS.name} onChange={(e) => set(k, { name: e.target.value })} />
          <input aria-label="Floor height" type="number" min={1} className="vtt-input w-16 px-1" value={Math.round(f.height * perSquare * 100) / 100} onChange={(e) => set(k, { height: Math.max(0.2, Number(e.target.value) / perSquare) })} />
          <label title="A roof over it"><input type="checkbox" checked={f.ceiling} onChange={(e) => set(k, { ceiling: e.target.checked })} /> ceiling</label>
          <label title="Open air: hides nothing below it"><input type="checkbox" checked={f.open === true} onChange={(e) => { const { open: _o, ...r } = f; setDraft(draft.map((x, i) => (i === k ? (e.target.checked ? { ...r, open: true } : r) : x))); }} /> open air</label>
          <label title="The scene's ground: height 0"><input type="radio" name="ground" checked={(draft.find((x) => x.ground) ?? draft[0])?.id === f.id} onChange={() => setDraft(draft.map(({ ground: _g, ...x }) => (x.id === f.id ? { ...x, ground: true } : x)))} /> ground</label>
          <select aria-label="Floor map" className="vtt-input max-w-[9rem] px-1" value={f.map?.id ?? ''} data-testid="floor-map"
            onChange={(e) => { const a = maps.find((m) => m.id === e.target.value); const { map: _m, ...r } = f; setDraft(draft.map((x, i) => (i === k ? (a ? { ...r, map: { id: a.id, name: a.name, width: a.width, height: a.height } } : r) : x))); }}>
            <option value="">{(draft.find((x) => x.ground) ?? draft[0])?.id === f.id ? "The scene's map" : 'No map'}</option>
            {maps.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button type="button" className={btn} disabled={k === 0} aria-label="Lower" onClick={() => move(k, -1)}>↓</button>
          <button type="button" className={btn} disabled={k === draft.length - 1} aria-label="Raise" onClick={() => move(k, 1)}>↑</button>
          <button type="button" className={btn} disabled={draft.length === 1} onClick={() => setDraft(draft.filter((_, i) => i !== k))}>Remove</button>
        </div>
      ))}
      {draft.length < FLOOR_LIMITS.floors && <button type="button" className={btn} onClick={() => setDraft([...draft, { id: newId(), name: `Floor ${draft.length + 1}`, height: DEFAULT_FLOORS[0]!.height, ceiling: true }])}>Add a floor</button>}
      {holding.map(({ f, things }) => (
        <label key={f.id} className="flex items-center gap-1 text-amber-300" data-testid="floor-removed-dest">
          {f.name} holds {things.length} {things.length === 1 ? 'thing' : 'things'}: move to
          <select className="vtt-input px-1" value={dest[f.id] ?? floorBelow(old, f.id, draft)} onChange={(e) => setDest({ ...dest, [f.id]: e.target.value })}>
            {draft.filter((d) => old.some((x) => x.id === d.id)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
      ))}
      <div className="flex gap-1">
        <button type="button" className={btn} disabled={!valid} onClick={() => {
          const moves = holding.flatMap(({ f, things }) => things.map((o) => ({ id: o.id, floor: dest[f.id] ?? floorBelow(old, f.id, draft) })));
          onSave(draft.map((f) => ({ ...f, name: f.name.trim() })), moves);
        }}>Save</button>
        <button type="button" className={btn} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
